import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Settings, Info } from 'lucide-react';
import { AvailabilityCalendar } from '@/components/host/availability/AvailabilityCalendar';
import { AvailabilitySettings } from '@/components/host/availability/AvailabilitySettings';
import { AvailabilityList } from '@/components/host/availability/AvailabilityList';
import { Alert, AlertDescription } from "@/components/ui/alert";

const Availability = () => {
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session?.user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        Loading...
      </div>
    );
  }

  if (!profile || profile.role !== 'host') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center">You must be a host to manage availability.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Manage Your Availability</h1>
          <p className="text-gray-600 mt-2">
            Set your available time slots and manage your booking preferences. You can offer both individual and group sessions.
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Quick Tip: Use the calendar to add new time slots, view them in list format, or adjust your hosting preferences in settings.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              List View
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Calendar View</CardTitle>
                <CardDescription>
                  Select dates to add or manage your availability. Click on a date to add new time slots for individual or group sessions.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <AvailabilityCalendar hostId={session!.user.id} isOwnProfile={true} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="list" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>List View</CardTitle>
                <CardDescription>
                  View all your scheduled availability slots chronologically. Manage individual and group sessions, pricing, and participant limits.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <AvailabilityList hostId={session!.user.id} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Availability Settings</CardTitle>
                <CardDescription>
                  Configure your hosting preferences, buffer times between sessions, and automatic booking settings.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <AvailabilitySettings hostId={session!.user.id} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Availability;